import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { BlotterDrawerWrap } from '@/App/Blotter/grids/details/BlotterDrawerWrap.tsx';
import { Fieldset } from '@/App/Blotter/grids/option/details/Fieldset.tsx';
import { StatusBadge } from '@/App/Blotter/grids/StatusBadge.tsx';
import { blotterSlice } from '@/store/blotter/slice.ts';
import type { BlotterOptionEntry } from '@/store/blotter/types/BlotterOptionEntry.ts';
import { useAppSelector } from '@/store/hooks.ts';

type Props = { tradeId: string; className?: string; onClose?: () => void };

export const DetailsDrawer = ({ tradeId, className, onClose }: Props) => {
  const entry = useAppSelector((state) => blotterSlice.selectors.getOptionEntryById(state, tradeId));

  const children = [] as ReactNode[];

  const currencyI18nValues = useMemo(() => ({ currency: getCurrencyToDisplay(entry) }), [entry]);

  if (isNotDefined(entry)) {
    children.push(<div key="error">Option trade id={tradeId} not found</div>);
  } else {
    children.push(
      <div key="title" className="display-5 mb-1 text-nowap" data-e2e="blotter-detailsPanel-title">
        {entry.product === 'Vanilla' ? (
          <>
            {entry.currencyPair}
            &nbsp;
            {isDefined(entry.optionType) ? (
              <FormattedMessage
                id={`blotter.tab.Option.details.optionType.${entry.optionType}`}
                values={currencyI18nValues}
              />
            ) : (
              entry.product
            )}
          </>
        ) : (
          <>
            <FormattedMessage id={`blotter.tab.Option.details.product.${entry.product}`} />
            &nbsp;
            {entry.currencyPair}
          </>
        )}
      </div>,
      <HStack key="idAndStatus" gap="8px" horizontalAlignment="start">
        <div className="fw-bold" data-e2e="blotter-detailsPanel-tradeId">
          ID: {tradeId}
        </div>
        <div data-e2e="blotter-detailsPanel-status">
          <StatusBadge status={entry.status} />
        </div>
      </HStack>,
      <div key="fieldset" className="h-100 overflow-y-auto overflow-x-hidden mt-2">
        <Fieldset entry={entry} />
      </div>,
    );
  }

  return (
    <BlotterDrawerWrap preventCloseButtonToHideContent className={className} onClose={onClose}>
      {children}
    </BlotterDrawerWrap>
  );
};

function getCurrencyToDisplay(entry: BlotterOptionEntry | undefined) {
  switch (entry?.optionType) {
    case 'CECSD1':
    case 'PECSD1':
      return entry.currencyPair.split('/')[0];
    case 'CECSD2':
    case 'PECSD2':
      return entry.currencyPair.split('/')[1];
  }
  return entry?.dealtCurrency ?? '';
}
